/**
 * Reading an image's pixel size from its bytes, without decoding it.
 *
 * A detection import needs `originalWidth` / `originalHeight` for every image,
 * because that is what the labeling canvas draws against and what training
 * divides by. Decoding a few hundred images through `<img>` or a canvas to
 * learn two numbers is slow, needs the DOM, and cannot be tested in jest. Every
 * container stores the size in its header, so this reads it directly.
 *
 * Pure over a `Uint8Array`, and it returns **null rather than a guess** for
 * anything it cannot parse. A wrong width here is not a visible failure — it
 * silently produces wrong boxes on every image in the dataset.
 *
 * Two subtleties that a naive implementation gets wrong, both verified against
 * hand-built fixtures (2026-09-11):
 *
 * - **A JPEG's segments must be WALKED, not scanned.** Searching the bytes for
 *   an SOF marker finds the one inside the EXIF thumbnail first, so a 1600x1200
 *   photo reports the size of its own 160x120 preview. The walk skips each
 *   segment by its declared length, so a thumbnail is never entered.
 *
 * - **EXIF orientation 5-8 means the image is displayed ROTATED.** The header
 *   then holds the pre-rotation size, while the browser — and so the labeling
 *   canvas — renders it transposed. Reporting the header's numbers would make
 *   every box on a rotated photo land at 90 degrees to its subject, so the
 *   width and height are swapped for those four orientations.
 */

/** SOF markers that carry a frame header. SOF0/1/2/3, 5/6/7, 9/10/11, 13/14/15. */
const SOF_MARKERS = new Set([
  0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf,
]);

/** Markers with NO length field: TEM, RSTn and SOI/EOI. */
const isStandalone = (marker) => marker === 0x01 || (marker >= 0xd0 && marker <= 0xd9);

/** Orientations where the displayed image is the transpose of the stored one. */
const TRANSPOSED_ORIENTATIONS = new Set([5, 6, 7, 8]);

const viewOf = (bytes) => new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

const matches = (bytes, offset, text) => {
  for (let i = 0; i < text.length; i += 1) {
    if (bytes[offset + i] !== text.charCodeAt(i)) return false;
  }
  return true;
};

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

export const readPngSize = (bytes) => {
  if (!bytes || bytes.length < 24) return null;
  for (let i = 0; i < 8; i += 1) {
    if (bytes[i] !== PNG_SIGNATURE[i]) return null;
  }
  const view = viewOf(bytes);
  // The first chunk of a PNG is always IHDR, and its length is always 13.
  if (view.getUint32(8, false) !== 13) return null;
  if (!matches(bytes, 12, 'IHDR')) return null;
  const width = view.getUint32(16, false);
  const height = view.getUint32(20, false);
  if (!width || !height) return null;
  return { width, height, format: 'png' };
};

export const readJpegSize = (bytes) => {
  if (!bytes || bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;
  const view = viewOf(bytes);
  let i = 2;
  while (i < bytes.length) {
    if (bytes[i] !== 0xff) return null; // desynced: not a marker boundary
    while (i < bytes.length && bytes[i] === 0xff) i += 1; // fill bytes
    if (i >= bytes.length) return null;
    const marker = bytes[i];
    i += 1;
    if (marker === 0x00) return null; // a stuffed byte means we lost the frame
    if (isStandalone(marker)) continue;
    if (i + 2 > bytes.length) return null;
    const length = view.getUint16(i, false);
    if (length < 2 || i + length > bytes.length) return null;
    if (SOF_MARKERS.has(marker)) {
      if (length < 8) return null;
      // frame header: precision(1) height(2) width(2) ...
      const height = view.getUint16(i + 3, false);
      const width = view.getUint16(i + 5, false);
      // height 0 means the real height arrives later in a DNL segment.
      if (!width || !height) return null;
      return { width, height, format: 'jpeg' };
    }
    if (marker === 0xda) return null; // start of scan, and no SOF was seen
    i += length; // skip the whole segment — this is what avoids the thumbnail
  }
  return null;
};

export const readWebpSize = (bytes) => {
  if (!bytes || bytes.length < 16) return null;
  if (!matches(bytes, 0, 'RIFF') || !matches(bytes, 8, 'WEBP')) return null;
  const view = viewOf(bytes);
  const riffEnd = Math.min(bytes.length, 8 + view.getUint32(4, true));
  let i = 12;
  while (i + 8 <= riffEnd) {
    const fourcc = String.fromCharCode(bytes[i], bytes[i + 1], bytes[i + 2], bytes[i + 3]);
    const size = view.getUint32(i + 4, true);
    const payload = i + 8;
    if (payload + size > bytes.length) return null;
    if (fourcc === 'VP8X') {
      if (size < 10) return null;
      // 24-bit little-endian, stored as (value - 1).
      const width = (bytes[payload + 4] | (bytes[payload + 5] << 8) | (bytes[payload + 6] << 16)) + 1;
      const height = (bytes[payload + 7] | (bytes[payload + 8] << 8) | (bytes[payload + 9] << 16)) + 1;
      return { width, height, format: 'webp' };
    }
    if (fourcc === 'VP8 ') {
      if (size < 10) return null;
      const tag = bytes[payload] | (bytes[payload + 1] << 8) | (bytes[payload + 2] << 16);
      if ((tag & 1) !== 0) return null; // not a key frame
      if (bytes[payload + 3] !== 0x9d || bytes[payload + 4] !== 0x01 || bytes[payload + 5] !== 0x2a) {
        return null; // missing start code
      }
      const width = view.getUint16(payload + 6, true) & 0x3fff;
      const height = view.getUint16(payload + 8, true) & 0x3fff;
      if (!width || !height) return null;
      return { width, height, format: 'webp' };
    }
    if (fourcc === 'VP8L') {
      if (size < 5 || bytes[payload] !== 0x2f) return null;
      const bits = view.getUint32(payload + 1, true);
      // 14 bits each, stored as (value - 1).
      return {
        width: (bits & 0x3fff) + 1,
        height: ((bits >>> 14) & 0x3fff) + 1,
        format: 'webp',
      };
    }
    // RIFF chunks are padded to an even length.
    i = payload + size + (size & 1);
  }
  return null;
};

/**
 * The EXIF orientation of a JPEG, 1-8, or null.
 *
 * Only the first IFD is searched, which is where the tag lives.
 */
export const readJpegOrientation = (bytes) => {
  if (!bytes || bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;
  const view = viewOf(bytes);
  let i = 2;
  while (i < bytes.length) {
    if (bytes[i] !== 0xff) return null;
    while (i < bytes.length && bytes[i] === 0xff) i += 1;
    if (i >= bytes.length) return null;
    const marker = bytes[i];
    i += 1;
    if (isStandalone(marker)) continue;
    if (i + 2 > bytes.length) return null;
    const length = view.getUint16(i, false);
    if (length < 2 || i + length > bytes.length) return null;
    if (marker === 0xe1 && i + 8 < bytes.length && matches(bytes, i + 2, 'Exif')) {
      const tiff = i + 8;
      if (tiff + 8 > bytes.length) return null;
      let little;
      if (matches(bytes, tiff, 'II')) little = true;
      else if (matches(bytes, tiff, 'MM')) little = false;
      else return null;
      if (view.getUint16(tiff + 2, little) !== 42) return null;
      const ifd = tiff + view.getUint32(tiff + 4, little);
      if (ifd + 2 > bytes.length) return null;
      const count = view.getUint16(ifd, little);
      for (let entry = 0; entry < count; entry += 1) {
        const at = ifd + 2 + entry * 12;
        if (at + 12 > bytes.length) return null;
        if (view.getUint16(at, little) === 0x0112) {
          const value = view.getUint16(at + 8, little);
          return value >= 1 && value <= 8 ? value : null;
        }
      }
      return null;
    }
    if (marker === 0xda) return null;
    i += length;
  }
  return null;
};

/**
 * The size the image will be DISPLAYED at, or null.
 *
 * For a JPEG whose EXIF says it is rotated a quarter turn, that is the
 * transpose of what the header holds — which is what a browser renders and so
 * what a bounding box must be measured against.
 */
export const readImageSize = (bytes) => {
  if (!bytes || !bytes.length) return null;
  const size = readPngSize(bytes) || readJpegSize(bytes) || readWebpSize(bytes);
  if (!size) return null;
  if (size.format === 'jpeg') {
    const orientation = readJpegOrientation(bytes);
    if (orientation && TRANSPOSED_ORIENTATIONS.has(orientation)) {
      return { width: size.height, height: size.width, format: size.format, rotated: true };
    }
  }
  return { ...size, rotated: false };
};

export default readImageSize;
